import { useState, useCallback, useEffect, useRef } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/contexts/PermissionContext";
import { validateRequiredDocuments } from "@/services/documentValidationService";
import { Stage } from "@/types/pipeline";
import { ExtendedProject } from "@/types/project";

// ============================================================================
// Types
// ============================================================================

/** Pipeline type for state management */
export interface StatePipeline {
  id: number;
  name: string;
  pipeline_type: string;
}

/** Options for the useProjectStateManagement hook */
export interface UseProjectStateManagementOptions {
  projectId: string | null;
  project: ExtendedProject | null;
  formValues: Partial<ExtendedProject>;
  /** Callback to update project state in parent */
  setProject: React.Dispatch<React.SetStateAction<ExtendedProject | null>>;
  /** Optional callback fired on status change */
  onStatusChange?: (oldStatus: string, newStatus: string) => void;
  /** Optional callback fired on stage change */
  onStageChange?: (oldStageId: number, newStageId: number) => void;
  /** Optional callback fired on pipeline change */
  onPipelineChange?: (oldPipelineId: number, newPipelineId: number) => void;
  /** Optional error callback */
  onError?: (error: Error) => void;
  /** Optional callback to set saving state in parent */
  setSaving?: (saving: boolean) => void;
  /** Optional callback to set save error in parent */
  setSaveError?: (error: string | null) => void;
  /** Optional callback to set save success in parent */
  setSaveSuccess?: (success: boolean) => void;
}

/** Return type for the useProjectStateManagement hook */
export interface UseProjectStateManagementReturn {
  // Dialog visibility states
  showStageDialog: boolean;
  showStatusDialog: boolean;
  showPipelineDialog: boolean;
  showPipelineStageDialog: boolean;

  // Dialog setters (with React.SetStateAction support for backward compatibility)
  setShowStageDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setShowStatusDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setShowPipelineDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setShowPipelineStageDialog: React.Dispatch<React.SetStateAction<boolean>>;

  // Dropdown states (for inline stage/status selectors)
  stageDropdownOpen: boolean;
  statusDropdownOpen: boolean;
  setStageDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setStatusDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  stageDropdownRef: React.RefObject<HTMLDivElement | null>;
  statusDropdownRef: React.RefObject<HTMLDivElement | null>;
  stageDropdownPosition: { top: number; left: number; width: number };
  setStageDropdownPosition: React.Dispatch<React.SetStateAction<{ top: number; left: number; width: number }>>;

  // Data
  stages: Stage[];
  pipelines: StatePipeline[];
  selectedPipelineId: number | null;

  // Data setters (for pipeline change flow)
  setStages: React.Dispatch<React.SetStateAction<Stage[]>>;
  setSelectedPipelineId: React.Dispatch<React.SetStateAction<number | null>>;

  // Operations
  handleStatusChange: (status: string) => Promise<void>;
  handleStageChange: (stageId: number) => Promise<void>;
  handlePipelineChange: (pipelineId: number) => Promise<void>;
  handlePipelineStageSelect: (stageId: number) => Promise<void>;

  // Data fetching
  fetchStages: () => Promise<void>;
  fetchPipelines: () => Promise<void>;
}

// ============================================================================
// Helper Functions
// ============================================================================

/** Helper function to check if project is residential */
const isResidentialProject = (formValues: Partial<ExtendedProject>) => {
  return (
    formValues?.project_type === "Residential" ||
    formValues?.project_type === "residential"
  );
};

// ============================================================================
// Hook Implementation
// ============================================================================

export function useProjectStateManagement({
  projectId,
  project,
  formValues,
  setProject,
  onStatusChange,
  onStageChange,
  onPipelineChange,
  onError,
  setSaving,
  setSaveError,
  setSaveSuccess,
}: UseProjectStateManagementOptions): UseProjectStateManagementReturn {
  const { user } = useAuth();
  const { roleInfo } = usePermissions();
  const { toast } = useToast();

  // ============================================================================
  // Dialog States
  // ============================================================================

  const [showStageDialog, setShowStageDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showPipelineDialog, setShowPipelineDialog] = useState(false);
  const [showPipelineStageDialog, setShowPipelineStageDialog] = useState(false);

  // ============================================================================
  // Dropdown States (for inline selectors in header)
  // ============================================================================

  const [stageDropdownOpen, setStageDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const stageDropdownRef = useRef<HTMLDivElement>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const [stageDropdownPosition, setStageDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  }>({ top: 0, left: 0, width: 0 });

  // ============================================================================
  // Data States
  // ============================================================================

  const [stages, setStages] = useState<Stage[]>([]);
  const [pipelines, setPipelines] = useState<StatePipeline[]>([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState<number | null>(null);

  // ============================================================================
  // Data Fetching
  // ============================================================================

  /** Fetch stages for current pipeline */
  const fetchStages = useCallback(async () => {
    if (!project?.pipeline_id) return;

    try {
      const { data: stagesData, error: stagesError } = await supabase
        .from("stages")
        .select("*")
        .eq("pipeline_id", project.pipeline_id)
        .order("stage_order", { ascending: true });

      if (stagesError) throw stagesError;

      setStages(stagesData || []);
    } catch (error) {
      console.error("Error fetching stages:", error);
      onError?.(error as Error);
    }
  }, [project?.pipeline_id, onError]);

  /** Fetch pipelines based on project type */
  const fetchPipelines = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("pipelines")
        .select("id, name, pipeline_type")
        .eq(
          "pipeline_type",
          isResidentialProject(formValues) ? "residential" : "commercial"
        )
        .order("name");

      if (error) throw error;
      setPipelines(data || []);
    } catch (error) {
      console.error("Error fetching pipelines:", error);
      toast({
        title: "Error",
        description: "Failed to fetch pipelines.",
        variant: "destructive",
      });
      onError?.(error as Error);
    }
  }, [formValues, toast, onError]);

  // ============================================================================
  // Effects
  // ============================================================================

  // Fetch stages when pipeline changes
  useEffect(() => {
    if (project?.pipeline_id) {
      fetchStages();
    }
  }, [project?.pipeline_id, fetchStages]);

  // Close status dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target as Node)
      ) {
        setStatusDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Update stage dropdown position when opened
  useEffect(() => {
    if (stageDropdownOpen && stageDropdownRef.current) {
      const rect = stageDropdownRef.current.getBoundingClientRect();
      setStageDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [stageDropdownOpen]);

  // ============================================================================
  // Status Change Handler
  // ============================================================================

  const handleStatusChange = useCallback(async (status: string) => {
    if (!projectId) return;

    try {
      const oldStatus = project?.project_status || "";

      const { error } = await supabase
        .from("projects")
        .update({ project_status: status })
        .eq("id", projectId);

      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
        onError?.(new Error(error.message));
      } else {
        toast({ title: "Success", description: "Project status updated." });

        // Update local state
        setProject((prev) =>
          prev ? { ...prev, project_status: status } : null
        );

        // Dispatch a custom event to notify the pipeline to refresh
        const refreshEvent = new CustomEvent("project-status-updated", {
          detail: { projectId, status },
        });
        document.dispatchEvent(refreshEvent);

        // Fire callback
        onStatusChange?.(oldStatus, status);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update project status.",
        variant: "destructive",
      });
      onError?.(error as Error);
    }
  }, [projectId, project?.project_status, setProject, toast, onStatusChange, onError]);

  // ============================================================================
  // Stage Change Handler
  // ============================================================================

  const handleStageChange = useCallback(async (stageId: number) => {
    if (!project) {
      toast({
        title: "Error",
        description: "Project data not loaded. Please try refreshing the page.",
        variant: "destructive",
      });
      return;
    }

    try {
      setSaving?.(true);
      setSaveError?.(null);

      // Get the stage names for logging
      const { data: stagesData, error: stagesError } = await supabase
        .from("stages")
        .select("id, name")
        .in("id", [project.current_stage_id, stageId]);

      if (stagesError) throw stagesError;

      const oldStage = stagesData.find(
        (s) => s.id === project.current_stage_id
      );
      const newStage = stagesData.find((s) => s.id === stageId);

      // Validate required documents before stage advancement (DOC-1.0)
      const validation = await validateRequiredDocuments(
        project.id,
        project.project_type || 'Commercial',
        project.commercial_type
      );

      if (!validation.valid) {
        // Check if user has permission to override validation
        const canOverride = roleInfo && (
          roleInfo.name === 'Admin' ||
          roleInfo.name === 'Superadmin' ||
          roleInfo.name === 'Internal PM' ||
          roleInfo.name === 'Internal PM Manager'
        );

        if (canOverride) {
          // Authorized user: show override confirmation dialog
          const userConfirmed = window.confirm(
            `Missing required documents: ${validation.missingDocuments.join(', ')}\n\n` +
            'This project is missing a power bill required for Back of Meter commercial projects.\n\n' +
            'Do you want to override and continue anyway?'
          );

          if (!userConfirmed) {
            setSaving?.(false);
            return;
          }

          // User confirmed override - log it for audit trail
          console.warn('OVERRIDE: Stage advancement without required documents', {
            projectId: project.id,
            projectName: project.project_name,
            userId: user?.id,
            userRole: roleInfo?.name,
            missingDocuments: validation.missingDocuments,
            timestamp: new Date().toISOString()
          });
        } else {
          // Unauthorized user: block without override option
          toast({
            title: "Missing Required Documents",
            description: `Cannot advance stage: ${validation.missingDocuments.join(', ')} required for Back of Meter projects`,
            variant: "destructive",
          });
          setSaving?.(false);
          return;
        }
      }

      // Update project with stage change and timestamp
      const { error } = await supabase
        .from("projects")
        .update({
          current_stage_id: stageId,
          stage_changed_at: new Date().toISOString(),
          last_updated_by_user: user?.id,
        })
        .eq("id", project.id);

      if (error) throw error;

      // Log the stage change in activity_logs
      const { error: logError } = await supabase.from("activity_logs").insert({
        project_id: project.id,
        user_id: user?.id,
        action_type: "stage_changed",
        details: {
          old_stage: {
            id: project.current_stage_id,
            name: oldStage?.name || "Unknown",
          },
          new_stage: {
            id: stageId,
            name: newStage?.name || "Unknown",
          },
        },
        timestamp: new Date().toISOString(),
        related_entity_type: "stage",
      });

      if (logError) {
        console.error("Error logging stage change:", logError);
      }

      // Update local state
      const updatedStage = stages.find((s) => s.id === stageId);
      if (updatedStage) {
        setProject((prev) =>
          prev
            ? {
                ...prev,
                current_stage_id: stageId,
                stage_changed_at: new Date().toISOString(),
                stages: {
                  id: updatedStage.id,
                  name: updatedStage.name,
                  pipeline_id: updatedStage.pipeline_id,
                  stage_order: updatedStage.stage_order,
                },
              }
            : null
        );
      }

      setSaveSuccess?.(true);
      toast({
        title: "Success",
        description: "Project stage updated successfully.",
      });

      // Dispatch a custom event to notify the pipeline to refresh
      const refreshEvent = new CustomEvent("project-stage-updated", {
        detail: { projectId: project.id, stageId },
      });
      document.dispatchEvent(refreshEvent);

      // Fire callback
      onStageChange?.(project.current_stage_id || 0, stageId);
    } catch (error) {
      console.error("Error updating project stage:", error);
      setSaveError?.("Failed to update project stage");
      toast({
        title: "Error",
        description: "Failed to update project stage.",
        variant: "destructive",
      });
      onError?.(error as Error);
    } finally {
      setSaving?.(false);
      setStageDropdownOpen(false);
    }
  }, [project, stages, user?.id, roleInfo, setProject, toast, setSaving, setSaveError, setSaveSuccess, onStageChange, onError]);

  // ============================================================================
  // Pipeline Change Handler (Step 1: Select pipeline, show stage dialog)
  // ============================================================================

  const handlePipelineChange = useCallback(async (pipelineId: number) => {
    if (!project) return;

    try {
      setSaving?.(true);
      setSaveError?.(null);

      // Get stages for the new pipeline to show in stage selection dialog
      const { data: stagesData, error: stagesError } = await supabase
        .from("stages")
        .select("*")
        .eq("pipeline_id", pipelineId)
        .order("stage_order", { ascending: true });

      if (stagesError) throw stagesError;

      if (!stagesData || stagesData.length === 0) {
        toast({
          title: "Error",
          description: "The selected pipeline has no stages. Please add stages first.",
          variant: "destructive",
        });
        return;
      }

      // Store the selected pipeline and show stage selection dialog
      // Don't update database yet - wait for user to select a stage
      setSelectedPipelineId(pipelineId);
      setStages(stagesData);
      setShowPipelineDialog(false);
      setShowPipelineStageDialog(true);
    } catch (error) {
      console.error("Error loading pipeline stages:", error);
      toast({
        title: "Error",
        description: "Failed to load pipeline stages.",
        variant: "destructive",
      });
      onError?.(error as Error);
    } finally {
      setSaving?.(false);
    }
  }, [project, toast, setSaving, setSaveError, onError]);

  // ============================================================================
  // Pipeline + Stage Select Handler (Step 2: Apply both changes)
  // ============================================================================

  const handlePipelineStageSelect = useCallback(async (stageId: number) => {
    if (!project || !selectedPipelineId) return;

    const isPipelineChange = selectedPipelineId !== project.pipeline_id;

    try {
      setSaving?.(true);
      setSaveError?.(null);

      // Get pipeline and stage names for logging
      const { data: pipelinesData } = await supabase
        .from("pipelines")
        .select("id, name")
        .in("id", [project.pipeline_id, selectedPipelineId]);

      const { data: stagesData, error: stagesError } = await supabase
        .from("stages")
        .select("id, name")
        .in("id", [project.current_stage_id, stageId].filter(Boolean));

      if (stagesError) throw stagesError;

      const oldPipeline = pipelinesData?.find((p) => p.id === project.pipeline_id);
      const newPipeline = pipelinesData?.find((p) => p.id === selectedPipelineId);
      const oldStage = stagesData?.find((s) => s.id === project.current_stage_id);
      const newStage = stagesData?.find((s) => s.id === stageId);

      // Update project with both pipeline_id and current_stage_id together
      // This satisfies the database constraint that validates stage belongs to pipeline
      const { error } = await supabase
        .from("projects")
        .update({
          pipeline_id: selectedPipelineId,
          current_stage_id: stageId,
          stage_changed_at: new Date().toISOString(),
          last_updated_by_user: user?.id,
        })
        .eq("id", project.id);

      if (error) throw error;

      // Log pipeline change if applicable
      if (isPipelineChange) {
        const { error: pipelineLogError } = await supabase.from("activity_logs").insert({
          project_id: project.id,
          user_id: user?.id,
          action_type: "pipeline_changed",
          details: {
            old_pipeline: {
              id: project.pipeline_id,
              name: oldPipeline?.name || "Unknown",
            },
            new_pipeline: {
              id: selectedPipelineId,
              name: newPipeline?.name || "Unknown",
            },
          },
          timestamp: new Date().toISOString(),
          related_entity_type: "pipeline",
        });

        if (pipelineLogError) {
          console.error("Error logging pipeline change:", pipelineLogError);
        }

        // Fire pipeline change callback
        onPipelineChange?.(project.pipeline_id || 0, selectedPipelineId);
      }

      // Log stage change
      const { error: stageLogError } = await supabase.from("activity_logs").insert({
        project_id: project.id,
        user_id: user?.id,
        action_type: "stage_changed",
        details: {
          old_stage: {
            id: project.current_stage_id,
            name: oldStage?.name || "Unknown",
          },
          new_stage: {
            id: stageId,
            name: newStage?.name || "Unknown",
          },
        },
        timestamp: new Date().toISOString(),
        related_entity_type: "stage",
      });

      if (stageLogError) {
        console.error("Error logging stage change:", stageLogError);
      }

      // Update local state
      const updatedStage = stages.find((s) => s.id === stageId);
      if (updatedStage) {
        setProject((prev) =>
          prev
            ? {
                ...prev,
                pipeline_id: selectedPipelineId,
                current_stage_id: stageId,
                stage_changed_at: new Date().toISOString(),
                stages: {
                  id: updatedStage.id,
                  name: updatedStage.name,
                  pipeline_id: updatedStage.pipeline_id,
                  stage_order: updatedStage.stage_order,
                },
              }
            : null
        );
      }

      setSaveSuccess?.(true);
      toast({
        title: "Success",
        description: isPipelineChange
          ? "Project moved to new pipeline successfully."
          : "Project stage updated successfully.",
      });

      // Dispatch a custom event to notify the pipeline to refresh
      const refreshEvent = new CustomEvent("project-stage-updated", {
        detail: { projectId: project.id, stageId, pipelineId: selectedPipelineId },
      });
      document.dispatchEvent(refreshEvent);

      // Fire stage change callback
      onStageChange?.(project.current_stage_id || 0, stageId);
    } catch (error) {
      console.error("Error updating project:", error);
      setSaveError?.(isPipelineChange ? "Failed to move project to new pipeline" : "Failed to update project stage");
      toast({
        title: "Error",
        description: isPipelineChange ? "Failed to move project to new pipeline." : "Failed to update project stage.",
        variant: "destructive",
      });
      onError?.(error as Error);
    } finally {
      setSaving?.(false);
      setShowPipelineStageDialog(false);
    }
  }, [project, selectedPipelineId, stages, user?.id, setProject, toast, setSaving, setSaveError, setSaveSuccess, onStageChange, onPipelineChange, onError]);

  // ============================================================================
  // Return Hook Value
  // ============================================================================

  return {
    // Dialog visibility states
    showStageDialog,
    showStatusDialog,
    showPipelineDialog,
    showPipelineStageDialog,

    // Dialog setters
    setShowStageDialog,
    setShowStatusDialog,
    setShowPipelineDialog,
    setShowPipelineStageDialog,

    // Dropdown states
    stageDropdownOpen,
    statusDropdownOpen,
    setStageDropdownOpen,
    setStatusDropdownOpen,
    stageDropdownRef,
    statusDropdownRef,
    stageDropdownPosition,
    setStageDropdownPosition,

    // Data
    stages,
    pipelines,
    selectedPipelineId,

    // Data setters
    setStages,
    setSelectedPipelineId,

    // Operations
    handleStatusChange,
    handleStageChange,
    handlePipelineChange,
    handlePipelineStageSelect,

    // Data fetching
    fetchStages,
    fetchPipelines,
  };
}
